import type { PLADataService, PLAElementRecord, PLAProject, PLARecord, PLARelationRecord } from "./PLAState";

export function getPLAStaleRelations(project: PLAProject, records: PLARecord[]): PLARelationRecord[] {
   const elements = new Map(records
      .filter((record): record is PLAElementRecord => record.recordType === "ELEMENT")
      .map((element) => [element.id, element]));
   const validEndpoint = (id: string) => {
      const element = elements.get(id);
      return element !== undefined && Object.prototype.hasOwnProperty.call(project.elementTypes, element.elementType);
   };
   return records.filter((record): record is PLARelationRecord =>
      record.recordType === "RELATION" && (!validEndpoint(record.fromId) || !validEndpoint(record.toId)));
}

export async function cleanupPLAStaleRelations(project: PLAProject, service: Pick<PLADataService, "loadAllRecords" | "deleteRelation" | "deleteElement">) {
   // Never infer missing endpoints from a potentially truncated table load.
   const records = await service.loadAllRecords();
   const staleRelations = getPLAStaleRelations(project, records);
   const orphanedElements = records.filter((record): record is PLAElementRecord =>
      record.recordType === "ELEMENT" && !Object.prototype.hasOwnProperty.call(project.elementTypes, record.elementType));
   const deletedIds = new Set<string>();
   const errors: { recordId: string; message: string }[] = [];
   for (const relation of staleRelations) {
      try {
         await service.deleteRelation(relation.id);
         deletedIds.add(relation.id);
      } catch (error) {
         errors.push({ recordId: relation.id, message: error instanceof Error ? error.message : String(error) });
      }
   }
   const remainingRelations = staleRelations.filter((relation) => !deletedIds.has(relation.id));
   let deletedElements = 0;
   for (const element of orphanedElements) {
      if (remainingRelations.some((relation) => relation.fromId === element.id || relation.toId === element.id)) {
         errors.push({ recordId: element.id, message: "Orphaned element not deleted because related relations could not be deleted" });
         continue;
      }
      try {
         await service.deleteElement(element.id);
         deletedIds.add(element.id);
         deletedElements++;
      } catch (error) {
         errors.push({ recordId: element.id, message: error instanceof Error ? error.message : String(error) });
      }
   }
   return {
      scanned: records.filter((record) => record.recordType === "RELATION").length,
      stale: staleRelations.length,
      deleted: deletedIds.size - deletedElements,
      orphanedElements: orphanedElements.length,
      deletedElements,
      errors,
      records: records.filter((record) => !deletedIds.has(record.id))
   };
}
